package pl.decks.personal.components.widgets.tag

import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import pl.decks.personal.styles.SiteLayout

object TagStylesheet {
    object Vars {
        val textColor by StyleVariable<Color>()
        val textInactiveColor by StyleVariable<Color>()
        val textInteractionColor by StyleVariable<Color>()

        val chipOutlineWidth by StyleVariable<CSSLengthNumericValue>(SiteLayout.Line.Normal)
        val chipOutlineStyle by StyleVariable(LineStyle.Solid)
        val chipOutlineColor by StyleVariable<Color>()

        val chipBackgroundColor by StyleVariable<Color>(Colors.Transparent)
        val chipHoverBackgroundColor by StyleVariable<Color>(Colors.Transparent)
        val chipActiveBackgroundColor by StyleVariable<Color>()
        val chipSelectedBackgroundColor by StyleVariable<Color>()

        val tagsGap by StyleVariable(SiteLayout.Space.XSmall)

        val textTransitionDuration by StyleVariable(TransitionDurationVars.VeryFast.value())
        val backgroundTransitionDuration by StyleVariable(TransitionDurationVars.Normal.value())
    }

    object Tag {
        sealed interface Kind : ComponentKind

        val Style = CssStyle.base<Kind> {
            Modifier
                .color(Vars.textColor.value(colorMode.toPalette().color))
                .transition {
                    property("color")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(Vars.textTransitionDuration.value())
                }
        }

        val ActiveVariant = Style.addVariant {
            val interactionModifier = Modifier.color(
                Vars.textInteractionColor.value(
                    Vars.textColor.value(
                        colorMode.toPalette().color.toRgb().copyf(alpha = 0.75f)
                    )
                )
            )
            hover { interactionModifier }
            active { interactionModifier }
        }
        val InactiveVariant = Style.addVariant {
            val fallbackColor = colorMode.toPalette().color.toRgb()
            val interactionModifier = Modifier.color(
                Vars.textInteractionColor.value(
                    Vars.textColor.value(
                        fallbackColor.copyf(alpha = 0.75f)
                    )
                )
            )
            base {
                Modifier.color(Vars.textInactiveColor.value(fallbackColor.copyf(alpha = 0.5f)))
            }
            hover { interactionModifier }
            active { interactionModifier }
        }
        val UncoloredVariant = Style.addVariantBase {
            Modifier.setVariable(Vars.textColor, colorMode.toPalette().color)
        }
        val InversedVariant = Style.addVariantBase {
            val invColor = colorMode.toPalette().background.toRgb()
            Modifier
                .setVariable(Vars.textColor, invColor)
                .setVariable(Vars.textInteractionColor, invColor.copyf(alpha = 0.75f))
                .setVariable(Vars.textInactiveColor, invColor.copyf(alpha = 0.5f))
        }
    }

    object Chip {
        sealed interface Kind : ComponentKind

        val Outline = CssStyle.base<Kind> {
            Modifier
                .padding(leftRight = SiteLayout.Space.Relative.Small)
                .borderRadius(SiteLayout.Rounding.Full)
                .border(
                    Vars.chipOutlineWidth.value(),
                    Vars.chipOutlineStyle.value(),
                    Vars.chipOutlineColor.value(colorMode.toPalette().border)
                )
                .transition {
                    property("background-color")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(Vars.backgroundTransitionDuration.value())
                }
        }
        val RestingVariant = Outline.addVariant {
            base {
                Modifier.backgroundColor(Vars.chipBackgroundColor.value())
            }
            hover {
                Modifier.backgroundColor(Vars.chipHoverBackgroundColor.value())
            }
            active {
                Modifier.backgroundColor(
                    Vars.chipActiveBackgroundColor.value(
                        Vars.chipOutlineColor.value(colorMode.toPalette().border)
                    )
                )
            }
        }
        val SelectedVariant = Outline.addVariant {
            base {
                Modifier.backgroundColor(
                    Vars.chipSelectedBackgroundColor.value(
                        Vars.chipOutlineColor.value(colorMode.toPalette().border)
                    )
                )
            }
            hover {
                Modifier.backgroundColor(
                    Vars.chipActiveBackgroundColor.value(
                        Vars.chipHoverBackgroundColor.value()
                    )
                )
            }
            active {
                Modifier.backgroundColor(
                    Vars.chipSelectedBackgroundColor.value(
                        Vars.chipOutlineColor.value(colorMode.toPalette().border)
                    )
                )
            }
        }
    }

    val Listing = CssStyle.base {
        Modifier
            .flexWrap(FlexWrap.Wrap)
            .gap(Vars.tagsGap.value())
    }
}
